/**
 * Notification Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 522-58
 *
 * A simple notification banner component with support for:
 * - Five style variants: Success, Warning, Error, Neutral, Info
 * - Optional left and right icons
 * - Customizable notification text
 * - Color-coded by semantic meaning
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Icon, type IconName } from '../icons';
import { colors, typography, spacing, borderRadius } from '../tokens';

export interface NotificationProps {
  /**
   * Notification text message
   * @default "This is Gripp notification example"
   */
  text?: string;

  /**
   * Style variant for semantic meaning
   * @default "success"
   */
  style?: 'success' | 'warning' | 'error' | 'neutral' | 'info';

  /**
   * Left icon name
   */
  leftIcon?: IconName;

  /**
   * Show left icon
   * @default true
   */
  showLeftIcon?: boolean;

  /**
   * Right icon name
   */
  rightIcon?: IconName;

  /**
   * Show right icon
   * @default false
   */
  showRightIcon?: boolean;
}

export const Notification: React.FC<NotificationProps> = ({
  text = 'This is Gripp notification example',
  style = 'success',
  leftIcon = 'Check Circle',
  showLeftIcon = true,
  rightIcon = 'X',
  showRightIcon = false,
}) => {
  // Get background color using Crop Circle color tokens
  const getBackgroundColor = () => {
    switch (style) {
      case 'success':
        return colors.semantic.successLight; // #D3F4E7
      case 'warning':
        return colors.semantic.warningLight; // #FFF4C1
      case 'error':
        return colors.semantic.errorLight; // #FFD9D2
      case 'info':
        return colors.semantic.infoLight; // #D0E5F9
      case 'neutral':
      default:
        return colors.slate[100]; // #ECEEF1
    }
  };

  // Get text and icon color using Crop Circle color tokens
  const getTextColor = () => {
    switch (style) {
      case 'success':
        return colors.semantic.successDark; // #24835D
      case 'warning':
        return colors.semantic.warningDark; // #D7A321
      case 'error':
        return colors.semantic.error; // #E24D2A
      case 'info':
        return colors.semantic.infoDark; // #1F5B99
      case 'neutral':
      default:
        return colors.slate[600]; // #3A3F4D
    }
  };

  const backgroundColor = getBackgroundColor();
  const textColor = getTextColor();

  return (
    <View style={[styles.container, { backgroundColor }]}>
      <View style={styles.content}>
        {/* Left Section */}
        {showLeftIcon && leftIcon && (
          <Icon name={leftIcon} size={16} color={textColor} />
        )}

        {/* Text Section */}
        <Text style={[styles.text, { color: textColor }]} numberOfLines={1}>
          {text}
        </Text>
      </View>

      {/* Right Section */}
      {showRightIcon && rightIcon && (
        <Icon name={rightIcon} size={16} color={textColor} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: 24,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  text: {
    ...typography.body.small,
    flex: 1,
  },
});

Notification.displayName = 'Notification';
